import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const ParentForcePasswordChange = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [parent, setParent] = useState(() => location.state?.parent || JSON.parse(localStorage.getItem('tempParentUser') || 'null'));
  const [ready, setReady] = useState(false);
  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let stopped = false;
    fetch(`${API_ENDPOINTS.BASE_URL}/api/session/me`, { credentials: 'include', cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error('Please sign in with your parent account again.');
        const data = await response.json();
        if (stopped) return;
        if (data.role !== 'parent') throw new Error('Please sign in with your parent account.');
        setParent(data.user);
        if (!data.requiresPasswordChange) {
          localStorage.removeItem('tempParentUser');
          localStorage.setItem('parentUser', JSON.stringify(data.user));
          navigate('/parent/dashboard', { replace: true, state: { parent: data.user } });
          return;
        }
        setReady(true);
      }).catch(error => { if (!stopped) setError(error.message); });
    return () => { stopped = true; };
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!ready || !parent) return;
    setError('');
    if (form.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.PARENTS}/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: parent.email,
          newPassword: form.newPassword
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to change password');
        return;
      }
      localStorage.removeItem('tempParentUser');
      localStorage.setItem('parentUser', JSON.stringify(data.parent));
      navigate('/parent/dashboard', { replace: true, state: { parent: data.parent } });
    } catch (err) {
      setError('Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-xl shadow-lg w-full max-w-md p-6">
        <p className="text-sm font-bold text-sky-700 uppercase">Parent Portal</p>
        <h1 className="text-2xl font-black text-slate-950 mt-1">Password Change Required</h1>
        <p className="text-slate-600 mt-2">Create a new password to secure your parent account and continue.</p>
        {error && <div role="alert" className="mt-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error} {!ready && <button type="button" className="underline" onClick={() => navigate('/parent-login')}>Sign in</button>}</div>}
        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <input type="password" autoComplete="new-password" maxLength={256} value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-3" placeholder="New password" required />
          <input type="password" autoComplete="new-password" maxLength={256} value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-3" placeholder="Confirm new password" required />
          <button disabled={loading || !ready} className="w-full bg-blue-950 text-white py-3 rounded-lg font-bold disabled:opacity-60">
            {loading ? 'Saving...' : !ready ? 'Checking account...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ParentForcePasswordChange;
